import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Pencil, Trash2, X, FilePlus2, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArticleBody } from "@/pages/JournalArticle";

const CTAS = [
  { to: "/contact", label: "Talk to the team" },
  { to: "/home-value", label: "Get your home value" },
  { to: "/contact?interest=buying", label: "Start your home search" },
  { to: "/contact?interest=selling", label: "Plan your sale" },
  { to: "/contact?interest=events", label: "Get our events list" },
];
const EMPTY = { title: "", topic: "", dek: "", body: "", cta_to: "/contact", published: true };
const field = "w-full border border-[#E5E1DA] px-4 py-3 outline-none focus:border-[#d4a373] bg-white text-sm min-w-0";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.15em] text-[#2c5865]">{label}</span>
      {hint && <span className="block text-xs text-[#2c5865]/70 mt-0.5">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function EssayForm({ token, editing, onDone, onUnauthorized }) {
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPreview(false);
    setForm(editing ? {
      title: editing.title, topic: editing.topic, dek: editing.dek, body: editing.body,
      cta_to: editing.cta_to, published: editing.published,
    } : EMPTY);
  }, [editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const cta = CTAS.find((c) => c.to === form.cta_to);
    const payload = { ...form, cta_label: cta.label };
    setSaving(true);
    try {
      const cfg = { headers: { "X-Admin-Token": token } };
      if (editing) await api.put(`/journal/${editing.id}`, payload, cfg);
      else await api.post("/journal", payload, cfg);
      toast.success(form.published ? "Essay published" : "Draft saved");
      setForm(EMPTY);
      onDone();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please add a title and at least a few sentences");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#E5E1DA] p-5 md:p-8 space-y-4 min-w-0" data-testid="essay-form">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl text-[#1e4d5d]" style={{ fontFamily: "Playfair Display, serif" }}>{editing ? "Edit essay" : "Write an essay"}</h2>
        {editing && (
          <button type="button" onClick={() => onDone()} className="text-sm text-[#2c5865] flex items-center gap-1 hover:text-[#d4a373]" data-testid="essay-form-cancel">
            <X size={14} /> Cancel
          </button>
        )}
      </div>
      <Field label="Title *"><input className={field} value={form.title} onChange={set("title")} required minLength={3} data-testid="essay-title-input" /></Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Topic"><input className={field} value={form.topic} onChange={set("topic")} placeholder="Selling, Buying, Market…" data-testid="essay-topic-input" /></Field>
        <Field label="Button at the end">
          <select className={field} value={form.cta_to} onChange={set("cta_to")} data-testid="essay-cta-select">
            {CTAS.map((c) => <option key={c.to} value={c.to}>{c.label}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Short summary" hint="One or two sentences shown under the title."><textarea rows={2} className={field} value={form.dek} onChange={set("dek")} data-testid="essay-dek-input" /></Field>
      <Field label="Essay *" hint="Leave a blank line between paragraphs. Start a line with ## for a heading. Wrap words in *asterisks* for italics.">
        {preview
          ? <div className="border border-[#E5E1DA] p-5 max-h-[520px] overflow-auto bg-[#f8f5ee]" data-testid="essay-preview"><ArticleBody body={form.body || "Nothing to preview yet."} /></div>
          : <textarea rows={16} className={`${field} font-mono text-[13px] leading-relaxed`} value={form.body} onChange={set("body")} required minLength={20} data-testid="essay-body-input" />}
      </Field>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <label className="flex items-center gap-2 text-sm text-[#2c5865]">
          <input type="checkbox" checked={form.published} onChange={set("published")} data-testid="essay-published-toggle" />
          Publish on the website
        </label>
        <button type="button" onClick={() => setPreview((p) => !p)} className="text-sm text-[#1e4d5d] underline underline-offset-4 hover:text-[#d4a373]" data-testid="essay-preview-toggle">
          {preview ? "Back to editing" : "Preview"}
        </button>
      </div>
      <button className="btn-primary flex items-center gap-2" disabled={saving} data-testid="essay-form-submit">
        <FilePlus2 size={16} /> {saving ? "Saving…" : form.published ? (editing?.published ? "Save changes" : "Publish essay") : "Save draft"}
      </button>
    </form>
  );
}

export function JournalManager({ token, onUnauthorized }) {
  const [essays, setEssays] = useState([]);
  const [subs, setSubs] = useState(null);
  const [editing, setEditing] = useState(null);
  const cfg = { headers: { "X-Admin-Token": token } };

  const load = useCallback(() => {
    const headers = { headers: { "X-Admin-Token": token } };
    api.get("/journal/admin/all", headers).then((r) => setEssays(r.data)).catch((err) => err.response?.status === 401 && onUnauthorized());
    api.get("/journal/admin/subscribers", headers).then((r) => setSubs(r.data.count)).catch(() => setSubs(null));
  }, [token, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const remove = async (es) => {
    if (!window.confirm(`Delete "${es.title}"? It will be removed from the website.`)) return;
    try {
      await api.delete(`/journal/${es.id}`, cfg);
      toast.success("Essay deleted");
      if (editing?.id === es.id) setEditing(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      toast.error("Could not delete");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] items-start" data-testid="journal-manager">
      <EssayForm token={token} editing={editing} onUnauthorized={onUnauthorized} onDone={() => { setEditing(null); load(); }} />
      <div className="space-y-4 min-w-0" data-testid="journal-admin-list">
        <div className="flex items-center gap-2 text-sm text-[#2c5865] bg-white border border-[#E5E1DA] px-5 py-4" data-testid="journal-subscriber-count">
          <Users size={16} className="text-[#d4a373]" />
          {subs === null ? "Loading subscribers…" : `${subs} ${subs === 1 ? "person gets" : "people get"} new essays by email`}
        </div>
        {essays.map((es) => (
          <div key={es.id} className={`bg-white border p-5 flex gap-4 items-center ${editing?.id === es.id ? "border-[#d4a373]" : "border-[#E5E1DA]"}`} data-testid={`journal-admin-item-${es.id}`}>
            <span className={`shrink-0 ${es.published ? "text-[#d4a373]" : "text-[#aaa]"}`} title={es.published ? "Published" : "Draft"}>
              {es.published ? <Eye size={18} /> : <EyeOff size={18} />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[#1e4d5d] font-medium truncate">{es.title}</p>
              <p className="text-xs text-[#2c5865]/80">{es.published ? "Published" : "Draft"} · {es.read} read{es.topic ? ` · ${es.topic}` : ""}</p>
            </div>
            {es.published && (
              <a href={`/resources/journal/${es.slug}`} target="_blank" rel="noopener noreferrer" aria-label="View" className="p-2 text-[#2c5865] hover:text-[#d4a373]" data-testid={`journal-admin-view-${es.id}`}>
                <ExternalLink size={16} />
              </a>
            )}
            <button onClick={() => { setEditing(es); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="Edit" className="p-2 text-[#2c5865] hover:text-[#d4a373]" data-testid={`journal-admin-edit-${es.id}`}>
              <Pencil size={16} />
            </button>
            <button onClick={() => remove(es)} aria-label="Delete" className="p-2 text-[#2c5865] hover:text-red-700" data-testid={`journal-admin-delete-${es.id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
